import { useEffect, useMemo, useRef, useState } from "react";
import ReportTable from "../ReportTable.component";
import ReportService from "../../../services/reports/Report.service";
import CustomReportService from "../../../services/reports/CustomReport.service";
import EpochMonthService from "../../../services/EpochMonth.service";
import { BaseReportParsedData, MoneyDataItem } from "../../../interfaces/reports/BaseReport.interface";
import * as XLSX from "xlsx";
import MultiSelect from "../../../tailwindComponents/ui/multi-select";
import { clientMapping as mrfDefaultSortsByClient } from "../classPerformance/customSorts/MRFDefaultSortsByClient";
import { classOrder as eccoClassOrder, getClassOrderIndex } from "../classPerformance/customSorts/EccoClothesCustomSort";

interface CustomGoalsReportProps {
  clientId: string | number;
  epochMonth: number | null;
}

const calculateImu = (inflowRetail: number, inflowCost: number): number => {
  if (inflowRetail === 0) return 0;
  return (inflowRetail - inflowCost) / inflowRetail;
}

const calculateMmu = (inflowRetail: number, inflowCost: number, markdownPercent: number): number => {
  if (inflowRetail === 0) return 0;
  const imu = calculateImu(inflowRetail, inflowCost);
  return imu - (markdownPercent * (1 - imu));
}

// Raw record based on GetCustomGoalsReportData.sql final SELECT
export interface CustomGoalsRaw {
  locationCode: string;
  locationName: string;
  classCode: string;
  className: string;
  category: string | null;
  categorySortOrder: number | null;
  salesLast12: number; // retail
  inventoryAvgLast12: number; // retail avg inventory
  inflowRetailLast12: number; // retail
  inflowCostLast12: number; // cost
  markdownsLast12: number; // cost
  imu: number; // this month IMU (0..1)
  costComplement: number; // this month cost complement (0..1)
  salesPlan: number; // next month sales plan
  inventoryBomPlanAvg: number; // next month avg BOM (retail)
  salesPlanNext12: number; // next 12 months sales plan (annual)
}

// Parsed type compatible with ReportService.groupByHierarchy
interface CustomGoalsParsedData extends BaseReportParsedData {
  salesLast12: MoneyDataItem;
  inventoryAvgLast12: MoneyDataItem;
  inflowRetailLast12: MoneyDataItem;
  inflowCostLast12: MoneyDataItem;
  salesPlan: MoneyDataItem;
  inventoryBomPlanAvg: MoneyDataItem;
  salesPlanNext12: MoneyDataItem;
  markdownsLast12: MoneyDataItem;

  imu?: number;
  costComplement?: number;
}

const HIERARCHY = ["category", "classCode"] as const;

function parseRaw(rows: CustomGoalsRaw[]): CustomGoalsParsedData[] {
  return (rows || []).map((r) => ({
    locationCode: r.locationCode,
    locationName: r.locationName,
    classCode: r.classCode,
    className: r.className,
    category: r.category ?? "N/A",
    categorySortOrder: r.categorySortOrder ?? 9999,
    parentClientClassCode: null,

    salesLast12: new MoneyDataItem(r.salesLast12 || 0),
    inventoryAvgLast12: new MoneyDataItem(r.inventoryAvgLast12 || 0),
    inflowRetailLast12: new MoneyDataItem(r.inflowRetailLast12 || 0),
    inflowCostLast12: new MoneyDataItem(r.inflowCostLast12 || 0),
    markdownsLast12: new MoneyDataItem(r.markdownsLast12 || 0),
    salesPlan: new MoneyDataItem(r.salesPlan || 0),
    inventoryBomPlanAvg: new MoneyDataItem(r.inventoryBomPlanAvg || 0),
    salesPlanNext12: new MoneyDataItem(r.salesPlanNext12 || 0),

    imu: r.imu ?? 0,
    costComplement: r.costComplement ?? (1 - (r.imu ?? 0)),
  }));
}

const CustomGoalsReport = ({ clientId, epochMonth }: CustomGoalsReportProps) => {
  const raw = useRef<CustomGoalsRaw[]>([]);
  const [parsed, setParsed] = useState<CustomGoalsParsedData[]>([]);
  const [grouped, setGrouped] = useState<CustomGoalsParsedData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  // filters
  // multi-select: empty or ['ALL'] means no filtering
  const [locationFilter, setLocationFilter] = useState<string[]>(["ALL"]);
  const [classSortMode, setClassSortMode] = useState<"alphanumeric" | "trailingLettersLast" | "noLetterClasses">("alphanumeric");

  const clientIdNum = Number(clientId);
  const isOrbit719 = clientIdNum === 3044;

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      try {
        const data = await CustomReportService.getCustomGoalsReportData({ clientId, epochMonth: epochMonth ?? EpochMonthService.getTodayEpochMonth() });
        raw.current = (data as CustomGoalsRaw[]) || [];
        const _parsed = parseRaw((data as CustomGoalsRaw[]) || []);
        setParsed(_parsed);
        const _grouped = ReportService.groupByHierarchy(_parsed, [...HIERARCHY]);
        setGrouped(_grouped);
      } catch (e) {
          setParsed([]);
          setGrouped([]);
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [clientId, epochMonth]);

  useEffect(() => {
    // ORBIT719 always uses custom ordering; prevent mapped overrides from changing it.
    if (clientIdNum === 3044) {
      setClassSortMode('alphanumeric');
      return;
    }

    // Apply per-client default class sort, if configured (mapping uses numeric clientId)
    const mapped = (mrfDefaultSortsByClient as any)?.[clientIdNum] as any;
    if (mapped) setClassSortMode(mapped);
  }, [clientIdNum]);

  const locations = useMemo(() => {
    const set = new Map<string, string>();
    parsed.forEach(p => {
      if (p.locationCode) set.set(p.locationCode, `${p.locationCode} ${p.locationName}`);
    });
    return [{ code: "ALL", label: "All Locations" }, ...Array.from(set.entries()).map(([code, label]) => ({ code, label }))];
  }, [parsed]);

  const filtered = useMemo(() => {
    // If 'ALL' is selected (or returned as default) treat as no filter (empty array)
    const locArr = (Array.isArray(locationFilter) && locationFilter.includes("ALL")) ? [] : (locationFilter || []);
    // class/category filters are not used in this component currently
    return ReportService.sortAndFilterData(parsed, [...HIERARCHY], locArr, [], []);
  }, [parsed, locationFilter]);

  const columns = useMemo(() => {
    return [
      { name: "Category", sortable: false },
      { name: "Class", sortable: false },
      { name: "Last 12 Sales", sortable: false },
      { name: "MMU", sortable: false },
      { name: "Turn", sortable: false },
      { name: "GMROI", sortable: false },
      { name: "Sales Annual Plan", sortable: false },
      { name: "Turn Plan", sortable: false },
      { name: "Sales Plan", sortable: false },
    ];
  }, []);

  const tableData = useMemo(() => {
    const baseRows = (filtered || []).map((r) => {
      const labels = ReportService.hierarchyItemToVerticalDisplayNames(r, [...HIERARCHY]);

      const salesLast12 = Number(r.salesLast12?.value) || 0;
      const invAvgLast12 = Number(r.inventoryAvgLast12?.value) || 0;
      const inflowRetail = Number(r.inflowRetailLast12?.value) || 0;
      const inflowCost = Number(r.inflowCostLast12?.value) || 0;
      const salesPlan = Number(r.salesPlan?.value) || 0;
      const invBomPlanAvg = Number(r.inventoryBomPlanAvg?.value) || 0;
      const salesPlanNext12 = Number(r.salesPlanNext12?.value) || 0;
      const markdownsLast12 = Number(r.markdownsLast12?.value) || 0;
      const markdownPercent = salesLast12 > 0 ? (markdownsLast12 / salesLast12) : 0;

      const imu = calculateImu(inflowRetail, inflowCost) as number;
      const mmu = calculateMmu(inflowRetail, inflowCost, markdownPercent) as number;
      const turn = ReportService.calculateTurn({ sales: salesLast12, invAvg: invAvgLast12 }) as string;
      const gmroi = ReportService.calculateGmroi({
        sales: salesLast12,
        markdowns: markdownsLast12,
        costComplement: 1 - (Number(imu) || 0),
        imu: Number(imu) || 0,
        invAvgCost: invAvgLast12 * (1 - (Number(imu) || 0)),
      }) as string;

      return [
        ...labels,
        ReportService.formatCurrency(salesLast12),
        ReportService.formatPercent(((Number(mmu) || 0) * 100)),
        turn,
        gmroi,
        ReportService.formatCurrency(salesPlanNext12),
        ReportService.calculateTurn({ sales: salesPlanNext12, invAvg: invBomPlanAvg }) as string,
        ReportService.formatCurrency(salesPlan),
      ];
    });

    const hasAnyLetters = (code: string) => /[a-zA-Z]/.test((code || '').trim());
    const compareAlphanumeric = (a: string, b: string) =>
      (a || '').localeCompare((b || ''), undefined, { numeric: true, sensitivity: 'base' });

    // Updated behavior: ALL codes that contain letters sort after codes that are strictly numeric.
    // Within each bucket (numeric-only vs contains-letters), keep standard alphanumeric ordering.
    const compareLettersBucketLast = (a: string, b: string) => {
      const aHasLetters = hasAnyLetters(a);
      const bHasLetters = hasAnyLetters(b);
      if (aHasLetters !== bHasLetters) return aHasLetters ? 1 : -1;
      return compareAlphanumeric(a, b);
    };

    const compareClassCode = (a: string, b: string) => {
      switch (classSortMode) {
        case 'alphanumeric':
          return compareAlphanumeric(a, b);
        case 'noLetterClasses':
          // sorting is still standard; filtering done separately
          return compareAlphanumeric(a, b);
        case 'trailingLettersLast':
        default:
          return compareLettersBucketLast(a, b);
      }
    };

    // Custom in-group sort: within each Category, keep "TOTAL" row(s), then sort class rows.
    // For ORBIT719, apply Ecco clothes class order within the category group.
    const sortedRows: Array<Array<string | number>> = [];
    let i = 0;
    while (i < baseRows.length) {
      const currentCategory = baseRows[i]?.[0];
      const group: Array<Array<string | number>> = [];
      while (i < baseRows.length && baseRows[i]?.[0] === currentCategory) {
        group.push(baseRows[i]);
        i++;
      }

      if (typeof currentCategory === 'string' && currentCategory.toLowerCase() === 'total') {
        sortedRows.push(...group);
        continue;
      }

      const totals = group.filter(r => String(r[1] || '').toLowerCase() === 'total');
      let classes = group.filter(r => String(r[1] || '').toLowerCase() !== 'total');

      const getCode = (row: Array<string | number>): string => String(row[1] || '').split(' ')[0];

      // IMPORTANT: For ORBIT719 we do NOT apply the "noLetterClasses" filter.
      if (!isOrbit719 && classSortMode === 'noLetterClasses') {
        classes = classes.filter(r => !hasAnyLetters(getCode(r)));
      }

      if (isOrbit719) {
        const order = eccoClassOrder || [];
        classes.sort((a, b) => {
          const aCode = getCode(a);
          const bCode = getCode(b);
          const ai = getClassOrderIndex(aCode, order);
          const bi = getClassOrderIndex(bCode, order);
          if (ai !== bi) return ai - bi;
          return compareAlphanumeric(aCode, bCode);
        });
      } else {
        classes.sort((a, b) => compareClassCode(getCode(a), getCode(b)));
      }

      sortedRows.push(...totals, ...classes);
    }

    return sortedRows;
  }, [filtered, classSortMode, clientId, isOrbit719]);

  const rowClassName = (rowIndex: number) => {
    if (!tableData || rowIndex < 0 || rowIndex >= tableData.length) return null;
    return ReportService.getRowClassName(tableData[rowIndex], ["category", "classCode"]);
};

  const exportToExcel = () => {
    try {
      const header = columns.map(c => c.name);
      const ws = XLSX.utils.aoa_to_sheet([header, ...tableData]);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, 'CustomGoals');
      const fileName = `Custom_Goals_${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (e) {
      console.error(e);
    }
  };

  return (
    <div className="w-full h-full flex flex-col items-center justify-center text-center">
      <h2 className="text-2xl font-bold mb-2">
        {`${EpochMonthService.epochMonthToMMMYY(epochMonth ?? EpochMonthService.getTodayEpochMonth())} Custom Goals (Category by Class)`}
      </h2>
      <div className="w-full flex flex-wrap items-center justify-end pr-4 mb-2 gap-2">
        <div className="flex items-center gap-2 mr-auto pl-2">
          <label className="text-sm">Location:</label>
            <MultiSelect
              options={locations.map(l => ({ code: l.code, label: l.label }))}
              value={locationFilter}
              onChange={(v) => setLocationFilter(v)}
              placeholder="All Locations"
            />
        </div>

        {!isOrbit719 && (
          <div className="flex items-center gap-2">
            <label className="text-sm">Class sort:</label>
            <select
              className="h-9 px-2 border rounded-md text-sm"
              value={classSortMode}
              disabled={isOrbit719}
              title={isOrbit719 ? 'Custom class sort is enforced for this client' : undefined}
              onChange={(e) => setClassSortMode(e.target.value as any)}
            >
              <option value="alphanumeric">Alphanumeric</option>
              <option value="trailingLettersLast">Alphanumeric (letters last)</option>
              <option value="noLetterClasses">Alphanumeric (exclude letter classes)</option>
            </select>
          </div>
        )}

        <button
          onClick={exportToExcel}
          className="h-9 px-4 rounded-md text-[13px] font-semibold text-white bg-tw-primary hover:bg-sky-600 shadow transition"
        >
          Download Excel
        </button>
      </div>
      {loading ? (
        <p>Loading...</p>
      ) : (
        <div className="w-full h-full">
          <ReportTable
            columns={columns}
            data={tableData}
            rowClassName={rowClassName}
            headerClassName="bg-slate-700 !text-white !text-center py-2 px-4"
            stickyHeader={true}
            hierarchyDepth={HIERARCHY.length}
          />
        </div>
      )}
    </div>
  );
};

export default CustomGoalsReport;
